// organizationSlice.ts
import { createSlice } from '@reduxjs/toolkit';

import {
  PreEmploymentResponse,
  PresassessmentGroupData,
  RegionSwotReportResponse,
  SwotReport,
  UserCourseResponse,
  UserEngagementResponses,
  UserStats,
} from './organization.types';
import {
  fetchPreEmployment,
  fetchEngagement,
  fetchCourses,
  fetchStats,
  fetchSwot,
  fetchSwotReportAsPerAgeAndRegion,
  fetchPreAssessmentReportAsPerRegion,
} from './organizationthunk';

export interface OrganizationState {
  preEmployment: PreEmploymentResponse | null;
  engagement: UserEngagementResponses | null;
  courses: UserCourseResponse | null;
  stats: UserStats | null;
  swot: SwotReport | null;
  regionSwotReport: RegionSwotReportResponse | null;
  regionPreAssessmentReport: PresassessmentGroupData | null;
  preEmploymentLoader: boolean;
  engagementLoader: boolean;

  coursesLoader: boolean;

  statsLoader: boolean;

  swotLoader: boolean;

  regionSwotReportLoader: boolean;

  regionPreAssessmentReportLoader: boolean;

  error: string | null;
}

const initialState: OrganizationState = {
  preEmployment: null,
  engagement: null,
  courses: null,
  stats: null,
  swot: null,
  regionSwotReport: null,
  regionPreAssessmentReport: null,
  preEmploymentLoader: true,
  engagementLoader: true,
  coursesLoader: true,
  statsLoader: true,
  swotLoader: true,
  regionSwotReportLoader: true,
  regionPreAssessmentReportLoader: true,

  error: null,
};

const organizationSlice = createSlice({
  name: 'organizationDashboard',
  initialState,
  reducers: {
    resetOrganizationState: () => initialState,
  },
  extraReducers: (builder) => {
    // individual thunks
    builder
      // 🔹 PreEmployment
      .addCase(fetchPreEmployment.pending, (state) => {
        state.preEmploymentLoader = true;
      })
      .addCase(fetchPreEmployment.fulfilled, (state, action) => {
        state.preEmploymentLoader = false;
        state.preEmployment = action.payload;
      })
      .addCase(fetchPreEmployment.rejected, (state, action) => {
        state.preEmploymentLoader = false;
        state.error = action.error.message || 'Failed to fetch pre-employment data';
      })

      // 🔹 Engagement
      .addCase(fetchEngagement.pending, (state) => {
        state.engagementLoader = true;
      })
      .addCase(fetchEngagement.fulfilled, (state, action) => {
        state.engagementLoader = false;
        state.engagement = action.payload;
      })
      .addCase(fetchEngagement.rejected, (state, action) => {
        state.engagementLoader = false;
        state.error = action.error.message || 'Failed to fetch engagement data';
      })

      // 🔹 Courses
      .addCase(fetchCourses.pending, (state) => {
        state.coursesLoader = true;
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.coursesLoader = false;
        state.courses = action.payload;
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.coursesLoader = false;
        state.error = action.error.message || 'Failed to fetch courses data';
      })

      // 🔹 Stats
      .addCase(fetchStats.pending, (state) => {
        state.statsLoader = true;
      })
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.statsLoader = false;
        state.stats = action.payload;
      })
      .addCase(fetchStats.rejected, (state, action) => {
        state.statsLoader = false;
        state.error = action.error.message || 'Failed to fetch stats data';
      })

      // 🔹 SWOT
      .addCase(fetchSwot.pending, (state) => {
        state.swotLoader = true;
      })
      .addCase(fetchSwot.fulfilled, (state, action) => {
        state.swotLoader = false;
        state.swot = action.payload;
      })
      .addCase(fetchSwot.rejected, (state, action) => {
        state.swotLoader = false;
        state.error = action.error.message || 'Failed to fetch SWOT data';
      })

      // 🔹 Region SWOT
      .addCase(fetchSwotReportAsPerAgeAndRegion.pending, (state) => {
        state.regionSwotReportLoader = true;
      })
      .addCase(fetchSwotReportAsPerAgeAndRegion.fulfilled, (state, action) => {
        state.regionSwotReportLoader = false;
        state.regionSwotReport = action.payload;
      })
      .addCase(fetchSwotReportAsPerAgeAndRegion.rejected, (state, action) => {
        state.regionSwotReportLoader = false;
        state.error = action.error.message || 'Failed to fetch region SWOT report';
      })

      // 🔹 PreAssessment Region
      // 🔹 PreAssessment Region
      .addCase(fetchPreAssessmentReportAsPerRegion.fulfilled, (state, action) => {
        state.regionPreAssessmentReportLoader = false;
        state.regionPreAssessmentReport = action.payload;
      })
      .addCase(fetchPreAssessmentReportAsPerRegion.pending, (state) => {
        state.regionPreAssessmentReportLoader = true;
      })
      .addCase(fetchPreAssessmentReportAsPerRegion.rejected, (state, action) => {
        state.regionPreAssessmentReportLoader = false;
        state.error = action.error.message || 'Failed to fetch region pre-assessment report';
      });
  },
});

export const { resetOrganizationState } = organizationSlice.actions;
export default organizationSlice.reducer;
